// Editor especializado pras etapas pizza_size/pizza_flavor/pizza_border dentro
// da aba "Personalização" do produto pizza-mãe. Renderizado em Products.js
// quando step.kind começa com "pizza_".
//
// Visual: paridade 1:1 com etapas de combo na mesma tela:
//   - Sub-header em pílulas: [Mín: X] [Máx: Y] [Tipo: ...] (read-only nas
//     etapas pizza, pq Mín/Máx são derivados das regras)
//   - Search bar verde (busca por sabor/borda/tamanho)
//   - "N items na etapa" + botões de ordenação [A→Z][Z→A][R$↑][R$↓]
//   - Cards horizontais uniformes em 1 linha: [↑↓] [#N] [foto] [nome+código]
//     [chips de preço VERDES no estilo do combo] [lixeira hover]
import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, Trash2, Pizza, RefreshCw, Search, ArrowUp, ArrowDown, Slice, Layers, Infinity as InfinityIcon } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

// =============================================================================
// CONFIRM DELETE DIALOG — modal de confirmação reutilizável (substitui window.confirm)
// Padrão de uso: setPendingDelete({index, label}) abre. onConfirm executa, null fecha.
// =============================================================================
function ConfirmDeleteDialog({ pending, onCancel, onConfirm, kind = "item" }) {
  const label = pending?.label || `${kind} #${(pending?.index ?? 0) + 1}`;
  return (
    <AlertDialog open={pending !== null} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir "{label}"?</AlertDialogTitle>
          <AlertDialogDescription>
            Esta ação não pode ser desfeita. O {kind} será removido desta etapa imediatamente.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            Excluir
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function getAuthHeader() {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

const fmtR$ = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
function parsePrice(s) {
  if (s == null || s === "") return 0;
  const clean = String(s).replace(/\./g, "").replace(",", ".").replace(/[^0-9.-]/g, "");
  const n = parseFloat(clean);
  return Number.isFinite(n) ? n : 0;
}

function getImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `/api${url}`;
  return url;
}

function moveInArray(arr, idx, dir) {
  const next = [...arr];
  const newIdx = dir === "up" ? idx - 1 : idx + 1;
  if (newIdx < 0 || newIdx >= next.length) return next;
  [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
  return next;
}

// =============================================================================
// SUB-HEADER de etapa — mesma estética das pílulas de Mín/Máx/Soma do combo.
// Mostra valores read-only nas etapas pizza (regras são derivadas, não editáveis).
// =============================================================================
function StepSubHeaderRow({ min, max, type, extraNote }) {
  return (
    <div className="flex flex-wrap gap-3 items-center">
      <div className="flex items-center gap-2 bg-gray-100 dark:bg-zinc-700 rounded-lg px-3 py-2">
        <span className="text-xs text-muted-foreground">Mín:</span>
        <span className="h-7 w-14 flex items-center justify-center text-sm font-semibold bg-white dark:bg-zinc-600 rounded border border-border">{min}</span>
      </div>
      <div className="flex items-center gap-2 bg-gray-100 dark:bg-zinc-700 rounded-lg px-3 py-2">
        <span className="text-xs text-muted-foreground">Máx:</span>
        <span className="h-7 w-14 flex items-center justify-center text-sm font-semibold bg-white dark:bg-zinc-600 rounded border border-border">{max}</span>
      </div>
      {type && (
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-zinc-700 rounded-lg px-3 py-2">
          <span className="text-xs text-muted-foreground">Tipo:</span>
          <span className="text-sm font-semibold">{type}</span>
        </div>
      )}
      {extraNote && (
        <span className="text-xs text-muted-foreground">{extraNote}</span>
      )}
    </div>
  );
}

// =============================================================================
// PRICE INPUT — input controlado pra preço em pt-BR (vírgula como separador).
// Mantém state local da string digitada pra preservar a vírgula durante o
// typing — o bug clássico de "digitei 14, mas só fica 14" some assim.
// Re-sincroniza com value externo SÓ quando o número difere (não a string).
// =============================================================================
function PriceInput({ value, onChange, placeholder = "0,00", className }) {
  // SEMPRE 2 casas decimais no display inicial (79.9 → "79,90"). Durante typing,
  // o state local toma conta — usuário pode digitar "14," sem perder a vírgula.
  const fmt = (v) => {
    const n = Number(v) || 0;
    return n > 0 ? n.toFixed(2).replace(".", ",") : "";
  };
  const [local, setLocal] = useState(() => fmt(value));

  useEffect(() => {
    const localNum = parsePrice(local);
    const valueNum = Number(value) || 0;
    if (Math.abs(localNum - valueNum) > 0.001) setLocal(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    // Permite só dígitos e vírgula. Limita a 1 vírgula. Aceita ponto também,
    // mas converte pra vírgula visualmente (pra paste de "14.90" funcionar).
    let s = e.target.value.replace(/\./g, ",").replace(/[^\d,]/g, "");
    const firstComma = s.indexOf(",");
    if (firstComma !== -1) {
      s = s.slice(0, firstComma + 1) + s.slice(firstComma + 1).replace(/,/g, "");
    }
    // Limita a 2 casas decimais pós-vírgula
    if (firstComma !== -1) {
      const [int, dec] = s.split(",");
      s = int + "," + (dec || "").slice(0, 2);
    }
    setLocal(s);
    onChange(parsePrice(s));
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={local}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
    />
  );
}

// =============================================================================
// SEARCH BAR — verde com glow, fundo verde escuro translúcido (estilo combo).
// Lupa dupla: ícone + emoji 🔍 dentro do placeholder pra reforçar.
// =============================================================================
function PizzaSearchBar({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500 z-10" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`🔍  ${placeholder}`}
        className="pl-10 h-11 bg-green-500/[0.04] dark:bg-green-900/15 border-green-500/40 focus-visible:border-green-500 focus-visible:ring-green-500/20 placeholder:text-green-700/60 dark:placeholder:text-green-300/40"
      />
    </div>
  );
}

// =============================================================================
// PIZZA STEP BANNER — banner laranja explicativo no topo de TODAS as etapas
// pizza_*. Uniforme: ícone Pizza + título "Etapa automática para PIZZA" + sub
// (descrição contextual por etapa).
// =============================================================================
function PizzaStepBanner({ description }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-500/[0.06] border border-orange-500/25">
      <div className="w-9 h-9 rounded-lg bg-orange-500/15 text-orange-500 flex items-center justify-center flex-shrink-0">
        <Pizza className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-orange-700 dark:text-orange-300">Etapa automática para PIZZA</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
  );
}

// =============================================================================
// COUNTER + SORTER — "N items na etapa" + botões de ordenação (igual combo)
// =============================================================================
function CounterAndSorter({ count, label, sortMode, onSortChange }) {
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap text-sm">
      <div className="text-muted-foreground">
        <span className="font-semibold text-foreground">{count}</span> {label}
      </div>
      {onSortChange && (
        <div className="flex items-center gap-1 text-xs">
          <span className="text-muted-foreground mr-1">Ordenar:</span>
          {[
            { id: "az", label: "A→Z" },
            { id: "za", label: "Z→A" },
            { id: "priceAsc", label: "R$↑" },
            { id: "priceDesc", label: "R$↓" },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onSortChange(sortMode === s.id ? "" : s.id)}
              className={`h-7 px-2 rounded text-[11px] font-semibold border ${sortMode === s.id ? "bg-orange-500 text-white border-orange-500" : "bg-muted hover:bg-muted/70 border-border"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PRICE CHIPS — 1 chip por tamanho. Cada chip: [TAMANHO_LABEL] [PriceInput].
// `accent`: "green" (sabores — vem do catálogo, parece "automático") ou
// "orange" (bordas — sempre criadas manualmente, sem ser sincronizado).
// PriceInput interno mantém string digitada (preserva vírgula).
// =============================================================================
function PriceChips({ sizes, pricesBySize, onChangeCell, accent = "green" }) {
  if (!sizes || sizes.length === 0) return null;
  const palette = accent === "orange"
    ? {
        on: "border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20",
        text: "text-orange-700 dark:text-orange-400",
      }
    : {
        on: "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20",
        text: "text-green-700 dark:text-green-400",
      };
  // Abaixo de md os preços descem para a linha de baixo (order-last + largura
  // cheia): na mesma linha do nome, o preço do 2º tamanho ficava fora da tela.
  return (
    <div className="order-last md:order-none w-full md:w-auto flex flex-wrap md:flex-nowrap items-center justify-end gap-1.5 flex-shrink-0">
      {sizes.map((sz) => {
        const value = pricesBySize?.[sz.id];
        const hasPrice = value != null && Number(value) > 0;
        return (
          <div
            key={sz.id}
            className={`flex items-center h-8 rounded border overflow-hidden ${hasPrice ? palette.on : "border-border bg-muted/30"}`}
            title={sz.name}
          >
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-1 text-muted-foreground border-r border-border/40">
              {sz.name}
            </span>
            <PriceInput
              value={value}
              onChange={(v) => onChangeCell(sz.id, v)}
              className={`h-8 w-16 text-sm text-right tabular-nums font-semibold border-0 px-1.5 bg-transparent ${hasPrice ? palette.text : "text-muted-foreground"}`}
            />
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// STEPPER — input numérico com [−] [N] [+]. Mais intuitivo que digitar no input
// pra valores pequenos (fatias 1-12, sabores 1-4). Inclui ícone + label.
// =============================================================================
function Stepper({ icon, label, value, onChange, min = 1, max = 99, title }) {
  const n = Math.max(min, Number(value) || min);
  return (
    // Celular: rótulo em cima e − N + embaixo, para caber dois lado a lado.
    <div
      title={title}
      className="flex flex-col md:flex-row items-stretch md:items-center gap-1 md:gap-2 bg-muted/40 rounded-lg border border-border px-2 py-1 hover:border-orange-300 transition-colors"
    >
      <div className="flex items-center justify-center gap-1 text-muted-foreground">
        {icon}
        <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex items-center justify-between md:justify-start gap-1">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, n - 1))}
          disabled={n <= min}
          className={`w-8 h-8 md:w-6 md:h-6 rounded flex items-center justify-center text-muted-foreground ${
            n <= min ? "opacity-30 cursor-not-allowed" : "hover:bg-orange-500 hover:text-white"
          }`}
          aria-label={`Diminuir ${label}`}
        >
          <Minus className="w-3 h-3" />
        </button>
        <span className="font-bold text-sm w-6 text-center tabular-nums">{n}</span>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, n + 1))}
          disabled={n >= max}
          className={`w-8 h-8 md:w-6 md:h-6 rounded flex items-center justify-center text-muted-foreground ${
            n >= max ? "opacity-30 cursor-not-allowed" : "hover:bg-orange-500 hover:text-white"
          }`}
          aria-label={`Aumentar ${label}`}
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// SIZE EDITOR — Tamanho (pizza_size)
// Etapa especial de PIZZA: cliente sempre escolhe 1 tamanho (mín=1, máx=1
// fixados, nem exibidos na UI). A "regra de meio-a-meio" (maior/média) vive
// no step pizza_size mas será editada na etapa SABORES em iteração futura.
// =============================================================================
function SizeEditor({ step, flavorStep, motherProductId, allPizzaProducts = [], onChange, onChangeFlavorStep }) {
  const items = step.items || [];
  const [pendingDelete, setPendingDelete] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  // Consolida tamanhos existentes de TODAS as outras pizzas (dedupe por nome
  // case-insensitive), filtrando os que já estão neste step. Fonte principal
  // pro picker "Adicionar tamanho" (v1.5.325+ — evita duplicar tamanhos iguais
  // e melhora consistência com o card de edição em massa "Personalização Pizzas").
  const existingSizeOptions = useMemo(() => {
    const _norm = (s) => String(s || "").trim().toLowerCase();
    const alreadyNames = new Set((items || []).map((i) => _norm(i.name)).filter(Boolean));
    const byName = new Map(); // nome_lower → { name, slices, max_flavors, usedIn: [pizzaName] }
    for (const p of (allPizzaProducts || [])) {
      let steps;
      try {
        const raw = p.order_steps;
        steps = Array.isArray(raw) ? raw : (typeof raw === "string" ? JSON.parse(raw || "[]") : []);
      } catch (_) { continue; }
      if (!Array.isArray(steps)) continue;
      const sizeStep = steps.find((s) => s?.kind === "pizza_size");
      if (!sizeStep || !Array.isArray(sizeStep.items)) continue;
      for (const sz of sizeStep.items) {
        const nm = String(sz.name || "").trim();
        if (!nm) continue;
        const key = _norm(nm);
        if (alreadyNames.has(key)) continue;
        if (byName.has(key)) {
          byName.get(key).usedIn.push(p.name);
          continue;
        }
        byName.set(key, {
          name: nm,
          slices: sz.slices || 8,
          max_flavors: sz.max_flavors || 1,
          usedIn: [p.name],
        });
      }
    }
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allPizzaProducts, items]);

  // Mantém mín/máx fixos em 1 invisível pro usuário, mas garantido no estado
  // pra runtime não precisar inferir.
  useEffect(() => {
    if (step.min_selections !== 1 || step.max_selections !== 1) {
      onChange({ ...step, min_selections: 1, max_selections: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Item da pizza-mãe no step pizza_flavor — fonte do preço por tamanho EXIBIDO
  // aqui. O campo R$ na linha de cada tamanho é espelho de
  // flavorStep.items[where product_id === motherProductId].prices_by_size[size_id].
  // Os outros sabores têm preço próprio (editáveis no FlavorEditor ou no atalho
  // do editor de cada sabor).
  const motherFlavorItem = useMemo(() => {
    if (!motherProductId || !flavorStep || !Array.isArray(flavorStep.items)) return null;
    return flavorStep.items.find(it => it.product_id === motherProductId) || null;
  }, [flavorStep, motherProductId]);

  const setMotherPrice = (sizeId, newNum) => {
    if (!motherProductId || !flavorStep || !onChangeFlavorStep) return;
    const items2 = (flavorStep.items || []).map(it => {
      if (it.product_id !== motherProductId) return it;
      return { ...it, prices_by_size: { ...(it.prices_by_size || {}), [sizeId]: newNum } };
    });
    onChangeFlavorStep({ ...flavorStep, items: items2 });
  };

  const updateItem = (idx, patch) => onChange({ ...step, items: items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  const moveItem = (idx, dir) => onChange({ ...step, items: moveInArray(items, idx, dir) });
  // Adiciona um tamanho existente (nome + slices + max_flavors copiados) ou um em branco.
  const addExistingSize = (opt) => {
    onChange({ ...step, items: [...items, {
      id: uid(),
      name: opt.name,
      slices: opt.slices || 8,
      max_flavors: opt.max_flavors || 1,
    }] });
    setShowPicker(false);
  };
  const addBlankSize = () => {
    onChange({ ...step, items: [...items, { id: uid(), name: "", slices: 8, max_flavors: 2 }] });
    setShowPicker(false);
  };
  const askRemove = (idx) => {
    const it = items[idx];
    setPendingDelete({ index: idx, label: (it && it.name) || null });
  };
  const confirmRemove = () => {
    if (pendingDelete === null) return;
    const idx = pendingDelete.index;
    onChange({ ...step, items: items.filter((_, i) => i !== idx) });
    setPendingDelete(null);
  };

  return (
    <div className="space-y-3">
      <PizzaStepBanner description="O cliente sempre escolhe 1 tamanho. Defina aqui as opções disponíveis." />

      {items.length === 0 ? (
        <div className="bg-muted/30 rounded-lg p-8 text-center border-2 border-dashed border-muted-foreground/20">
          <Pizza className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">Nenhum tamanho. Adicione abaixo.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it, idx) => {
            const esgotado = it.available === false;
            return (
            <div key={it.id || idx} className={`group flex flex-wrap md:flex-nowrap items-center gap-2 rounded-lg border p-2 transition-colors ${esgotado ? "bg-red-500/[0.04] border-red-500/30 opacity-80" : "bg-gray-50 dark:bg-zinc-800 border-border hover:border-orange-300"}`}>
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button type="button" onClick={() => moveItem(idx, "up")} disabled={idx === 0} aria-label="Mover para cima" className={`p-1.5 md:p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 ${idx === 0 ? "opacity-30 cursor-not-allowed" : ""}`}>
                  <ArrowUp className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => moveItem(idx, "down")} disabled={idx === items.length - 1} aria-label="Mover para baixo" className={`p-1.5 md:p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 ${idx === items.length - 1 ? "opacity-30 cursor-not-allowed" : ""}`}>
                  <ArrowDown className="w-3 h-3" />
                </button>
              </div>

              <span className="text-xs text-muted-foreground font-mono w-5 text-center flex-shrink-0">{idx + 1}</span>

              <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${esgotado ? "bg-red-500/10 text-red-500" : "bg-orange-500/10 text-orange-500"}`}>
                <Pizza className="w-5 h-5" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Input
                    value={it.name || ""}
                    onChange={(e) => updateItem(idx, { name: e.target.value })}
                    placeholder="Ex: Grande, Pequena"
                    className={`h-9 font-semibold border-0 bg-transparent px-0 focus-visible:ring-0 text-sm ${esgotado ? "line-through text-muted-foreground" : ""}`}
                  />
                  {esgotado && (
                    <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400 flex-shrink-0">
                      Esgotado
                    </span>
                  )}
                </div>
              </div>

              {/* Steppers visuais: Fatias | Sabores  +  Preço base do tamanho.
                  Abaixo de md descem para a linha de baixo — lado a lado com o
                  nome, o nome do tamanho sumia e o preço saía da tela. */}
              <div className="order-last md:order-none w-full md:w-auto grid grid-cols-2 md:flex md:items-center gap-2 flex-shrink-0">
                <Stepper
                  icon={<Slice className="w-3.5 h-3.5" />}
                  label="Fatias"
                  value={it.slices || 8}
                  onChange={(v) => updateItem(idx, { slices: v })}
                  min={1}
                  max={16}
                  title="Quantas fatias o cliente recebe"
                />
                <Stepper
                  icon={<Layers className="w-3.5 h-3.5" />}
                  label="Sabores"
                  value={it.max_flavors || 1}
                  onChange={(v) => updateItem(idx, { max_flavors: v })}
                  min={1}
                  max={8}
                  title="Máximo de sabores que o cliente pode escolher neste tamanho (meio-a-meio = 2)"
                />
                {/* Preço deste tamanho — ESPELHA prices_by_size[size_id] do item
                    da pizza-mãe em pizza_flavor. Editar aqui = editar no atalho
                    do editor da pizza-mãe = editar a linha dela no FlavorEditor.
                    Só renderiza se temos pizza-mãe identificada (motherProductId). */}
                {motherFlavorItem && (() => {
                  const motherPrice = motherFlavorItem.prices_by_size?.[it.id];
                  const hasPrice = motherPrice != null && Number(motherPrice) > 0;
                  return (
                    <div
                      title="Preço desta pizza-mãe neste tamanho. Sabores diferentes têm preço próprio (editáveis em Sabores)."
                      className={`col-span-2 md:col-span-1 flex items-center h-9 rounded-lg border overflow-hidden ${
                        hasPrice
                          ? "border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20"
                          : "border-border bg-muted/40"
                      }`}
                    >
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${hasPrice ? "text-orange-700 dark:text-orange-400" : "text-muted-foreground"} border-r ${hasPrice ? "border-orange-300/40 dark:border-orange-800/40" : "border-border/40"}`}>
                        R$
                      </span>
                      <PriceInput
                        value={motherPrice}
                        onChange={(newNum) => setMotherPrice(it.id, newNum)}
                        className={`h-9 flex-1 min-w-0 md:flex-none md:w-20 text-sm text-right tabular-nums font-semibold border-0 px-1.5 bg-transparent ${hasPrice ? "text-orange-700 dark:text-orange-400" : "text-muted-foreground"}`}
                      />
                    </div>
                  );
                })()}
              </div>

              {/* Switch esgotado/ativo individual desta pizza. Independente do
                  bulk "esgotar em todas as pizzas" (Personalização Pizzas card).
                  Aqui o controle é só pra ESTE produto. */}
              <div className="flex flex-col items-center gap-0.5 flex-shrink-0" title={esgotado ? "Reativar este tamanho nesta pizza" : "Esgotar este tamanho só nesta pizza"}>
                <span className={`text-[8px] font-bold uppercase tracking-wider ${esgotado ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}>
                  {esgotado ? "Esg." : "Ativo"}
                </span>
                <Switch
                  checked={!esgotado}
                  onCheckedChange={() => updateItem(idx, { available: esgotado ? true : false })}
                  className="data-[state=checked]:bg-green-500"
                />
              </div>

              <Button type="button" size="icon" variant="ghost" onClick={() => askRemove(idx)} aria-label="Remover tamanho" className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity flex-shrink-0">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            );
          })}
        </div>
      )}

      {/* Picker de tamanho existente (v1.5.325+). Ao clicar em "Adicionar tamanho",
          mostra lista consolidada de tamanhos únicos usados em OUTRAS pizzas, pra
          o lojista reaproveitar (evita "Grande" repetido em pizzas diferentes com
          slices/max_flavors ligeiramente diferentes). Última opção: criar do zero. */}
      {!showPicker ? (
        <Button type="button" variant="outline" onClick={() => setShowPicker(true)} className="w-full">
          <Plus className="w-4 h-4 mr-2" /> Adicionar tamanho
        </Button>
      ) : (
        <div className="rounded-lg border-2 border-orange-300 dark:border-orange-800 bg-orange-50/40 dark:bg-orange-900/10 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-orange-800 dark:text-orange-200">
              {existingSizeOptions.length > 0
                ? `Escolha um tamanho existente (${existingSizeOptions.length})`
                : "Nenhum tamanho existente em outras pizzas"}
            </p>
            <button
              type="button"
              onClick={() => setShowPicker(false)}
              className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-0.5 rounded"
            >
              Cancelar
            </button>
          </div>
          {existingSizeOptions.length > 0 && (
            <div className="grid gap-1 max-h-64 overflow-y-auto">
              {existingSizeOptions.map((opt) => (
                <button
                  key={opt.name}
                  type="button"
                  onClick={() => addExistingSize(opt)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg border bg-white dark:bg-zinc-800 hover:border-orange-400 hover:bg-orange-50/70 dark:hover:bg-orange-900/20 transition-all text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-orange-500 flex items-center justify-center flex-shrink-0">
                    <Pizza className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{opt.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {opt.slices} fatia{opt.slices === 1 ? "" : "s"} · até {opt.max_flavors} sabor{opt.max_flavors === 1 ? "" : "es"}
                      {opt.usedIn.length > 0 && ` · usado em ${opt.usedIn.length} pizza${opt.usedIn.length === 1 ? "" : "s"}`}
                    </p>
                  </div>
                  <Plus className="w-4 h-4 text-orange-500 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={addBlankSize}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border-2 border-dashed border-orange-400/50 hover:border-orange-500 hover:bg-orange-100/50 dark:hover:bg-orange-900/20 transition-colors text-orange-700 dark:text-orange-300"
          >
            <Plus className="w-4 h-4" />
            <span className="text-xs font-semibold">Criar um tamanho novo do zero</span>
          </button>
        </div>
      )}

      <ConfirmDeleteDialog
        pending={pendingDelete}
        kind="tamanho"
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmRemove}
      />
    </div>
  );
}

// =============================================================================
// FLAVOR EDITOR — Sabores (pizza_flavor)
// Quantidade (Mín/Máx) NÃO é editável aqui: derivada da etapa Tamanho
// (max_flavors do tamanho selecionado em runtime). UI mostra só a regra de
// preço pra meio-a-meio. pricing_rule é PERSISTIDO no step pizza_size (compat
// backend), editado aqui via onChangeSizeStep.
// =============================================================================
function FlavorEditor({ step, sizeStep, sizes, allPizzaProducts, motherProductId, onChange, onChangeSizeStep }) {
  const items = step.items || [];
  const [search, setSearch] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  // pricing_rule vive no step pizza_size (compat backend), editado aqui
  const pricingRule = sizeStep?.pricing_rule || "highest";
  const setPricingRule = (val) => {
    if (!sizeStep || !onChangeSizeStep) return;
    onChangeSizeStep({ ...sizeStep, pricing_rule: val });
  };

  const productById = useMemo(() => {
    const m = {};
    (allPizzaProducts || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [allPizzaProducts]);

  // Auto-recover + força sabor base no topo + HERANÇA AUTOMÁTICA contínua.
  // Roda quando items/productById/sizes mudam:
  //   1) Sabor próprio NÃO está em items → re-injeta como primeiro
  //   2) Sabor próprio está mas não em idx=0 → move pro topo
  //   3) Pra cada sabor (não-base) sem preço: busca na pizza fonte e completa
  //      (cada size só herda se o atual estiver vazio/zero — preserva edição manual)
  useEffect(() => {
    if (!motherProductId) return;
    if (!Array.isArray(items)) return;

    const ownIdx = items.findIndex(it => it?.product_id === motherProductId);

    // Caso 1: re-injeta sabor base se faltar.
    // Preços (v1.5.383): tenta herdar de OUTRA pizza que liste este produto
    // como sabor (match de tamanho por NOME); fallback: tamanho único usa o
    // sale_price do produto; senão fica vazio pro lojista preencher.
    if (ownIdx === -1) {
      const ownProduct = productById[motherProductId];
      if (!ownProduct) return;
      const recovered = {};
      const _norm = (s) => String(s || "").trim().toLowerCase();
      try {
        for (const other of (allPizzaProducts || [])) {
          if (!other || other.id === motherProductId) continue;
          const rawSteps = Array.isArray(other.order_steps)
            ? other.order_steps
            : JSON.parse(other.order_steps || "[]");
          if (!Array.isArray(rawSteps)) continue;
          const oSizes = rawSteps.find(s => s?.kind === "pizza_size")?.items || [];
          const oFlavor = rawSteps.find(s => s?.kind === "pizza_flavor");
          const meLa = (oFlavor?.items || []).find(x => x.product_id === motherProductId);
          if (!meLa || !meLa.prices_by_size) continue;
          for (const sz of (sizes || [])) {
            if (recovered[sz.id] != null) continue;
            const match = oSizes.find(os => _norm(os.name) === _norm(sz.name));
            if (match && meLa.prices_by_size[match.id] != null) {
              recovered[sz.id] = Number(meLa.prices_by_size[match.id]) || 0;
            }
          }
        }
      } catch (_) { /* herança falhou — segue pros fallbacks */ }
      if (!Object.keys(recovered).length && (sizes || []).length === 1 && Number(ownProduct.sale_price) > 0) {
        recovered[sizes[0].id] = Number(ownProduct.sale_price);
      }
      onChange({
        ...step,
        items: [
          { product_id: motherProductId, name: ownProduct.name, photo_url: ownProduct.photo_url, prices_by_size: recovered },
          ...items,
        ],
      });
      return;
    }

    // Caso 2: move sabor base pro topo
    if (ownIdx > 0) {
      const own = items[ownIdx];
      const others = items.filter((_, i) => i !== ownIdx);
      onChange({ ...step, items: [own, ...others] });
      return;
    }

    // Caso 3: SINCRONIZAÇÃO AUTOMÁTICA de preços pros sabores não-base.
    // O preço do sabor na PRÓPRIA pizza dele é a FONTE DA VERDADE — qualquer
    // pizza que usa esse sabor como meio-a-meio reflete o mesmo valor.
    // SOBRESCREVE preço atual (sem guard "só vazio") — evita divergência
    // tipo Carne Seca com Queijo Coalho R$89,90 nela mesma mas R$79,90 quando
    // aparece como sabor em Calabresa. Pra cobrar diferente meio-a-meio, edite
    // a pizza fonte.
    if (!sizes || sizes.length === 0) return;
    const _norm = (s) => String(s || "").trim().toLowerCase();
    let changed = false;
    const newItems = items.map((it) => {
      if (!it || it.product_id === motherProductId) return it;
      const sourceProduct = productById[it.product_id];
      if (!sourceProduct?.order_steps) return it;
      let sourceSteps;
      try {
        sourceSteps = Array.isArray(sourceProduct.order_steps)
          ? sourceProduct.order_steps
          : JSON.parse(sourceProduct.order_steps || "[]");
      } catch (_) { return it; }
      if (!Array.isArray(sourceSteps)) return it;
      const sourceSizes = sourceSteps.find(s => s?.kind === "pizza_size")?.items || [];
      const sourceFlavor = sourceSteps.find(s => s?.kind === "pizza_flavor");
      const ownInSource = (sourceFlavor?.items || []).find(x => x.product_id === it.product_id);
      const sourcePrices = ownInSource?.prices_by_size || {};
      if (!Object.keys(sourcePrices).length) return it;
      const currentPBS = { ...(it.prices_by_size || {}) };
      let itemChanged = false;
      for (const sz of sizes) {
        const sourceSize = sourceSizes.find(ss => _norm(ss.name) === _norm(sz.name));
        if (!sourceSize) continue;
        const sourceP = Number(sourcePrices[sourceSize.id] || 0);
        if (sourceP <= 0) continue; // sem preço na fonte, mantém atual
        const currentP = Number(currentPBS[sz.id] || 0);
        if (Math.abs(currentP - sourceP) < 0.001) continue; // já igual
        currentPBS[sz.id] = sourceP;
        itemChanged = true;
      }
      if (!itemChanged) return it;
      changed = true;
      return { ...it, prices_by_size: currentPBS };
    });
    if (changed) onChange({ ...step, items: newItems });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motherProductId, productById, items, sizes]);

  const updateItem = (idx, patch) => onChange({ ...step, items: items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  const moveItem = (idx, dir) => onChange({ ...step, items: moveInArray(items, idx, dir) });
  const askRemove = (idx) => {
    const it = items[idx];
    // Bloqueia delete do sabor PRÓPRIO (a pizza é seu próprio sabor base).
    // Ex: pizza "Calabresa" sempre tem "Calabresa" como sabor não-removível.
    if (motherProductId && it?.product_id === motherProductId) {
      toast.error("Este é o sabor base da pizza — não pode ser removido.");
      return;
    }
    const product = productById[it?.product_id] || {};
    setPendingDelete({ index: idx, label: product.name || it?.name || null });
  };
  const confirmRemove = () => {
    if (pendingDelete === null) return;
    const idx = pendingDelete.index;
    onChange({ ...step, items: items.filter((_, i) => i !== idx) });
    setPendingDelete(null);
  };
  const addItem = (product) => {
    if (!product?.id) return;
    if (items.some((it) => it.product_id === product.id)) { toast.error("Sabor já adicionado"); return; }

    // HERANÇA AUTOMÁTICA: ao adicionar o sabor X em outra pizza, herda o
    // prices_by_size que X tem em SI MESMO (na própria pizza X). Mapeia
    // size_ids da X pra size_ids da pizza atual via match POR NOME do tamanho
    // (cada pizza tem ids locais, mas nomes batem).
    const inheritedPrices = {};
    try {
      const raw = product.order_steps;
      const otherSteps = Array.isArray(raw) ? raw : (typeof raw === "string" ? JSON.parse(raw || "[]") : []);
      if (Array.isArray(otherSteps)) {
        const otherSizes = otherSteps.find(s => s?.kind === "pizza_size")?.items || [];
        const otherFlavor = otherSteps.find(s => s?.kind === "pizza_flavor");
        const ownInOther = (otherFlavor?.items || []).find(it => it.product_id === product.id);
        const ownPrices = ownInOther?.prices_by_size || {};
        const _norm = (s) => String(s || "").trim().toLowerCase();
        for (const sz of sizes) {
          const match = otherSizes.find(os => _norm(os.name) === _norm(sz.name));
          if (match && ownPrices[match.id] != null) {
            inheritedPrices[sz.id] = Number(ownPrices[match.id]) || 0;
          }
        }
      }
    } catch (_) { /* sem herança — começa vazio */ }

    onChange({ ...step, items: [...items, {
      product_id: product.id, name: product.name, photo_url: product.photo_url,
      prices_by_size: inheritedPrices,
    }] });
    setSearch("");
    const inheritedCount = Object.keys(inheritedPrices).length;
    if (inheritedCount > 0) {
      toast.success(`${product.name} adicionado — ${inheritedCount} preço${inheritedCount === 1 ? "" : "s"} herdado${inheritedCount === 1 ? "" : "s"} automaticamente`);
    }
  };
  const updatePriceCell = (idx, sizeId, price) => {
    const it = items[idx];
    updateItem(idx, { prices_by_size: { ...(it.prices_by_size || {}), [sizeId]: Number(price) || 0 } });
  };

  const inItems = new Set(items.map((it) => it.product_id));
  const searchLower = search.toLowerCase().trim();
  const availableToAdd = (allPizzaProducts || []).filter((p) =>
    !inItems.has(p.id) && (searchLower === "" || p.name?.toLowerCase().includes(searchLower))
  );

  if (!sizes || sizes.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-center">
        <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">Cadastre tamanhos primeiro</p>
        <p className="text-xs text-muted-foreground mt-1">Sabores precisam de preço por tamanho.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PizzaStepBanner description="A quantidade de sabores que o cliente escolhe é definida pelo TAMANHO selecionado. Aqui você só lista os sabores disponíveis." />

      {/* Sub-header SÓ com Tipo (regra de preço meio-a-meio). Sem Mín/Máx. */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-zinc-700 rounded-lg px-3 py-2">
          <span className="text-xs text-muted-foreground">Tipo:</span>
          {[
            { value: "highest", label: "Maior valor", title: "Pizza meio-a-meio cobra o sabor MAIS CARO" },
            { value: "lowest", label: "Menor valor", title: "Pizza meio-a-meio cobra o sabor MAIS BARATO" },
            { value: "average", label: "Média", title: "Pizza meio-a-meio cobra a MÉDIA dos sabores" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPricingRule(opt.value)}
              title={opt.title}
              className={`text-xs px-2.5 py-1 rounded font-semibold transition-colors ${
                pricingRule === opt.value
                  ? "bg-orange-500 text-white"
                  : "bg-white dark:bg-zinc-600 text-muted-foreground hover:bg-orange-50 dark:hover:bg-zinc-500"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-muted-foreground">
          regra usada quando o cliente monta uma pizza meio-a-meio (2+ sabores)
        </span>
      </div>

      <PizzaSearchBar value={search} onChange={setSearch} placeholder="Buscar produto para adicionar..." />

      {/* Sugestões enquanto digita */}
      {search && (
        <div className="space-y-1 -mt-1">
          {availableToAdd.length === 0 ? (
            <p className="text-[11px] text-muted-foreground text-center py-1">Nenhum sabor encontrado</p>
          ) : (
            <div className="grid gap-1">
              {availableToAdd.slice(0, 8).map((p) => (
                <button
                  key={p.id} type="button"
                  onClick={() => addItem(p)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg border hover:border-orange-400 hover:bg-orange-50/50 dark:hover:bg-orange-900/10 transition-all text-left"
                >
                  <div className="w-8 h-8 rounded overflow-hidden bg-gray-100 dark:bg-zinc-700 flex-shrink-0 flex items-center justify-center">
                    {p.photo_url ? <img src={getImageUrl(p.photo_url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span className="text-base">🍕</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{p.name}</p>
                    {p.code && <p className="text-[10px] text-muted-foreground">#{p.code}</p>}
                  </div>
                  <Plus className="w-4 h-4 text-orange-500 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-muted/30 rounded-lg p-8 text-center border-2 border-dashed border-muted-foreground/20">
          <p className="text-sm text-muted-foreground">Nenhum sabor adicionado. Use a busca acima.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it, idx) => {
            const product = productById[it.product_id] || {};
            const isSelf = motherProductId && it.product_id === motherProductId;
            // Sabor base SEMPRE em idx=0 e não move. Outros sabores nunca podem
            // ocupar idx=0 (reservado pro base) → bloqueia up no idx=1.
            const upDisabled = isSelf || idx === 0 || idx === 1;
            const downDisabled = isSelf || idx === items.length - 1;
            return (
              <div key={it.product_id || idx} className={`group flex flex-wrap md:flex-nowrap items-center gap-2 rounded-lg border p-2 transition-colors ${isSelf ? "bg-orange-500/[0.04] border-orange-400/50" : "bg-gray-50 dark:bg-zinc-800 border-border hover:border-orange-300"}`}>
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button type="button" onClick={() => moveItem(idx, "up")} disabled={upDisabled} aria-label="Mover para cima" className={`p-1.5 md:p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 ${upDisabled ? "opacity-30 cursor-not-allowed" : ""}`}>
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={() => moveItem(idx, "down")} disabled={downDisabled} aria-label="Mover para baixo" className={`p-1.5 md:p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 ${downDisabled ? "opacity-30 cursor-not-allowed" : ""}`}>
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>

                <span className="text-xs text-muted-foreground font-mono w-5 text-center flex-shrink-0">{idx + 1}</span>

                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-700 flex-shrink-0 flex items-center justify-center">
                  {(product.photo_url || it.photo_url) ? (
                    <img src={getImageUrl(product.photo_url || it.photo_url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (<span className="text-xl">🍕</span>)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="font-medium text-sm truncate">{product.name || it.name || "(sabor sem nome)"}</p>
                    {isSelf && (
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-orange-500 text-white flex-shrink-0">
                        Sabor base
                      </span>
                    )}
                  </div>
                  {product.code && <p className="text-xs text-muted-foreground">#{product.code}</p>}
                </div>

                <PriceChips sizes={sizes} pricesBySize={it.prices_by_size} onChangeCell={(sz, v) => updatePriceCell(idx, sz, v)} />

                {isSelf ? (
                  // Placeholder pra preservar o espaço da coluna lixeira (alinha
                  // visual com outros items). Title hover explica.
                  <div className="h-8 w-8 flex-shrink-0 flex items-center justify-center" title="Sabor base da pizza — indelével">
                    <span className="text-muted-foreground/40 text-xs">✕</span>
                  </div>
                ) : (
                  <Button type="button" size="icon" variant="ghost" onClick={() => askRemove(idx)} aria-label="Remover sabor" className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDeleteDialog
        pending={pendingDelete}
        kind="sabor"
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmRemove}
      />
    </div>
  );
}

// =============================================================================
// BORDER EDITOR — Bordas (pizza_border)
// =============================================================================
function BorderEditor({ step, sizes, allPizzaProducts = [], onChange }) {
  const items = step.items || [];
  const [pendingDelete, setPendingDelete] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  // Bordas existentes em OUTRAS pizzas — dedupe por nome, mostra prices_by_size
  // como preço médio pra dar contexto ao lojista. Mapeamento de size_ids na hora
  // de adicionar é feito por match de NOME (cada pizza tem ids locais dos tamanhos,
  // mas os nomes batem).
  const existingBorderOptions = useMemo(() => {
    const _norm = (s) => String(s || "").trim().toLowerCase();
    const alreadyNames = new Set((items || []).map((i) => _norm(i.name)).filter(Boolean));
    const byName = new Map(); // nome_lower → { name, sourceSizesByName: {sizeName_lower: price}, usedIn }
    for (const p of (allPizzaProducts || [])) {
      let steps;
      try {
        const raw = p.order_steps;
        steps = Array.isArray(raw) ? raw : (typeof raw === "string" ? JSON.parse(raw || "[]") : []);
      } catch (_) { continue; }
      if (!Array.isArray(steps)) continue;
      const borderStep = steps.find((s) => s?.kind === "pizza_border");
      const sizeStep = steps.find((s) => s?.kind === "pizza_size");
      if (!borderStep || !Array.isArray(borderStep.items)) continue;
      const sourceSizes = (sizeStep?.items) || [];
      for (const b of borderStep.items) {
        const nm = String(b.name || "").trim();
        if (!nm) continue;
        const key = _norm(nm);
        if (alreadyNames.has(key)) continue;
        // Traduz prices_by_size (chaveado por size_id local) pra chaveado por nome
        // do tamanho — assim conseguimos re-mapear pra o size_ids da pizza atual.
        const pricesByName = {};
        for (const sz of sourceSizes) {
          const p2 = Number(b.prices_by_size?.[sz.id] || 0);
          if (p2 > 0) pricesByName[_norm(sz.name)] = p2;
        }
        if (byName.has(key)) {
          byName.get(key).usedIn.push(p.name);
          // Herda preços novos só se ainda não tinha esse nome de tamanho.
          for (const [szKey, val] of Object.entries(pricesByName)) {
            if (byName.get(key).pricesByName[szKey] == null) {
              byName.get(key).pricesByName[szKey] = val;
            }
          }
          continue;
        }
        byName.set(key, { name: nm, pricesByName, usedIn: [p.name] });
      }
    }
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allPizzaProducts, items]);

  const updateItem = (idx, patch) => onChange({ ...step, items: items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  const moveItem = (idx, dir) => onChange({ ...step, items: moveInArray(items, idx, dir) });
  const askRemove = (idx) => {
    const it = items[idx];
    setPendingDelete({ index: idx, label: (it && it.name) || null });
  };
  const confirmRemove = () => {
    if (pendingDelete === null) return;
    const idx = pendingDelete.index;
    onChange({ ...step, items: items.filter((_, i) => i !== idx) });
    setPendingDelete(null);
  };
  // Adiciona borda existente (com prices_by_size remapeado por nome do tamanho)
  // ou uma em branco.
  const addExistingBorder = (opt) => {
    const _norm = (s) => String(s || "").trim().toLowerCase();
    const remapped = {};
    for (const sz of (sizes || [])) {
      const val = opt.pricesByName[_norm(sz.name)];
      if (val > 0) remapped[sz.id] = val;
    }
    onChange({ ...step, items: [...items, {
      id: uid(),
      name: opt.name,
      prices_by_size: remapped,
    }] });
    setShowPicker(false);
  };
  const addBlankBorder = () => {
    onChange({ ...step, items: [...items, { id: uid(), name: "", prices_by_size: {} }] });
    setShowPicker(false);
  };
  const updatePriceCell = (idx, sizeId, price) => {
    const it = items[idx];
    updateItem(idx, { prices_by_size: { ...(it.prices_by_size || {}), [sizeId]: Number(price) || 0 } });
  };

  if (!sizes || sizes.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-center">
        <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">Cadastre tamanhos primeiro</p>
        <p className="text-xs text-muted-foreground mt-1">Bordas têm preço por tamanho.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PizzaStepBanner description="Borda é opcional — o cliente escolhe nenhuma ou uma. Defina aqui as opções disponíveis com preço por tamanho." />

      {items.length === 0 ? (
        <div className="bg-muted/30 rounded-lg p-8 text-center border-2 border-dashed border-muted-foreground/20">
          <p className="text-sm text-muted-foreground">Nenhuma borda. Adicione abaixo.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it, idx) => {
            const esgotado = it.available === false;
            return (
            <div key={it.id || idx} className={`group flex flex-wrap md:flex-nowrap items-center gap-2 rounded-lg border p-2 transition-colors ${esgotado ? "bg-red-500/[0.04] border-red-500/30 opacity-80" : "bg-gray-50 dark:bg-zinc-800 border-border hover:border-orange-300"}`}>
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button type="button" onClick={() => moveItem(idx, "up")} disabled={idx === 0} aria-label="Mover para cima" className={`p-1.5 md:p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 ${idx === 0 ? "opacity-30 cursor-not-allowed" : ""}`}>
                  <ArrowUp className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => moveItem(idx, "down")} disabled={idx === items.length - 1} aria-label="Mover para baixo" className={`p-1.5 md:p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 ${idx === items.length - 1 ? "opacity-30 cursor-not-allowed" : ""}`}>
                  <ArrowDown className="w-3 h-3" />
                </button>
              </div>

              <span className="text-xs text-muted-foreground font-mono w-5 text-center flex-shrink-0">{idx + 1}</span>

              <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${esgotado ? "bg-red-500/10" : "bg-orange-500/10"}`}>
                <span className={`w-6 h-6 rounded-full border-[5px] ${esgotado ? "border-red-500/40" : "border-orange-500/70"}`} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Input
                    value={it.name || ""}
                    onChange={(e) => updateItem(idx, { name: e.target.value })}
                    placeholder="Ex: Catupiry, Cheddar"
                    className={`h-9 font-semibold border-0 bg-transparent px-0 focus-visible:ring-0 text-sm ${esgotado ? "line-through text-muted-foreground" : ""}`}
                  />
                  {esgotado && (
                    <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400 flex-shrink-0">
                      Esgotado
                    </span>
                  )}
                </div>
              </div>

              <PriceChips sizes={sizes} pricesBySize={it.prices_by_size} onChangeCell={(sz, v) => updatePriceCell(idx, sz, v)} accent="orange" />

              <div className="flex flex-col items-center gap-0.5 flex-shrink-0" title={esgotado ? "Reativar esta borda nesta pizza" : "Esgotar esta borda só nesta pizza"}>
                <span className={`text-[8px] font-bold uppercase tracking-wider ${esgotado ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}>
                  {esgotado ? "Esg." : "Ativo"}
                </span>
                <Switch
                  checked={!esgotado}
                  onCheckedChange={() => updateItem(idx, { available: esgotado ? true : false })}
                  className="data-[state=checked]:bg-green-500"
                />
              </div>

              <Button type="button" size="icon" variant="ghost" onClick={() => askRemove(idx)} aria-label="Remover borda" className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity flex-shrink-0">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            );
          })}
        </div>
      )}

      {/* Picker de borda existente (v1.5.325+). Ao clicar em "Adicionar borda",
          mostra lista consolidada de bordas únicas usadas em OUTRAS pizzas, com
          preços por tamanho remapeados por NOME (cada pizza tem size_ids locais).
          Última opção: criar borda nova do zero. */}
      {!showPicker ? (
        <Button type="button" variant="outline" onClick={() => setShowPicker(true)} className="w-full">
          <Plus className="w-4 h-4 mr-2" /> Adicionar borda
        </Button>
      ) : (
        <div className="rounded-lg border-2 border-orange-300 dark:border-orange-800 bg-orange-50/40 dark:bg-orange-900/10 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-orange-800 dark:text-orange-200">
              {existingBorderOptions.length > 0
                ? `Escolha uma borda existente (${existingBorderOptions.length})`
                : "Nenhuma borda existente em outras pizzas"}
            </p>
            <button
              type="button"
              onClick={() => setShowPicker(false)}
              className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-0.5 rounded"
            >
              Cancelar
            </button>
          </div>
          {existingBorderOptions.length > 0 && (
            <div className="grid gap-1 max-h-64 overflow-y-auto">
              {existingBorderOptions.map((opt) => {
                const priceCount = Object.keys(opt.pricesByName).length;
                const avgPrice = priceCount > 0
                  ? Object.values(opt.pricesByName).reduce((s, v) => s + v, 0) / priceCount
                  : 0;
                return (
                  <button
                    key={opt.name}
                    type="button"
                    onClick={() => addExistingBorder(opt)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg border bg-white dark:bg-zinc-800 hover:border-orange-400 hover:bg-orange-50/70 dark:hover:bg-orange-900/20 transition-all text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                      <span className="w-4 h-4 rounded-full border-[3px] border-orange-500/70" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{opt.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {avgPrice > 0 ? `preço médio ${fmtR$(avgPrice)}` : "sem preços"}
                        {opt.usedIn.length > 0 && ` · usada em ${opt.usedIn.length} pizza${opt.usedIn.length === 1 ? "" : "s"}`}
                      </p>
                    </div>
                    <Plus className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
          <button
            type="button"
            onClick={addBlankBorder}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border-2 border-dashed border-orange-400/50 hover:border-orange-500 hover:bg-orange-100/50 dark:hover:bg-orange-900/20 transition-colors text-orange-700 dark:text-orange-300"
          >
            <Plus className="w-4 h-4" />
            <span className="text-xs font-semibold">Criar uma borda nova do zero</span>
          </button>
        </div>
      )}

      <ConfirmDeleteDialog
        pending={pendingDelete}
        kind="borda"
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmRemove}
      />
    </div>
  );
}

// =============================================================================
// ROOT EXPORT
// =============================================================================
export default function PizzaStepEditor({ step, sizeStep, flavorStep, sizes, motherProductId, allPizzaProducts, onChange, onChangeSizeStep, onChangeFlavorStep }) {
  if (!step || !step.kind) return null;
  if (step.kind === "pizza_size") return (
    <SizeEditor
      step={step}
      flavorStep={flavorStep}
      motherProductId={motherProductId}
      allPizzaProducts={allPizzaProducts || []}
      onChange={onChange}
      onChangeFlavorStep={onChangeFlavorStep}
    />
  );
  if (step.kind === "pizza_flavor") return (
    <FlavorEditor
      step={step}
      sizeStep={sizeStep}
      sizes={sizes}
      allPizzaProducts={allPizzaProducts || []}
      motherProductId={motherProductId}
      onChange={onChange}
      onChangeSizeStep={onChangeSizeStep}
    />
  );
  if (step.kind === "pizza_border") return <BorderEditor step={step} sizes={sizes} allPizzaProducts={allPizzaProducts || []} onChange={onChange} />;
  return null;
}

export { fmtR$, parsePrice, PriceInput };
